// Maps a US license state to the DocuSign form provider treated as "form access
// available" for agents in that state. Presence in the account-wide provider list
// is enough — some accounts have the provider enabled without every user carrying
// a `provisioned: true` flag. Mirrors mobile's docusign.service.ts STATE_FORM_PROVIDER.
// This is specific to which states have an actual NRDS-linked provider integration
// configured on our DocuSign account — extend only when a state gets a real one.
// Confirmed against the live account (2026-07-13): only CA has a provider (car, nar,
// gar, nwmls, oref, tar also exist but aren't state-mapped here). Do not add a state
// without first checking GET /api/v1/docusign/form-providers.
const STATE_FORM_PROVIDER = {
  CA: 'car',
};

// Mirrors mobile's docusign.service.ts hasFormAccess() exactly: any explicitly
// provisioned provider satisfies readiness; otherwise fall back to checking whether
// the agent's license-state default provider (e.g. CAR for CA) is present at all.
export const hasFormAccess = (providers, licenseState) => {
  if (!Array.isArray(providers) || providers.length === 0) return false;
  if (providers.some((p) => p.provisioned === true)) return true;
  const expected = licenseState ? STATE_FORM_PROVIDER[licenseState.toUpperCase()] : undefined;
  if (!expected) return false;
  return providers.some((p) => (p.formProviderId || '').toLowerCase() === expected);
};

// Some states (e.g. Colorado/CREC) have no DocuSign form-provider integration at
// all on this account — their forms are only exposed as a form *library* (a plain
// browsable folder, no per-user provisioning concept). This is a second, independent
// readiness signal. Call it only when hasFormAccess() returns false, as a fallback,
// not a replacement — it needs no per-state entry, since the library lookup
// (GET /form-libraries?state=X) already matches all 50 states generically.
export const hasStateFormLibrary = async (licenseState, apiClient, API_ENDPOINTS, firebaseToken) => {
  if (!licenseState) return false;
  try {
    const response = await apiClient.get(API_ENDPOINTS.docusignFormLibraries, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      params: { state: licenseState },
    });
    const data = response?.data || response || [];
    const libraries = Array.isArray(data) ? data : (data.formsLibrarySummaries || data.formsLibraries || []);
    return libraries.some((lib) => (lib.formCount || 0) > 0);
  } catch {
    // Fail closed — an error here shouldn't be treated as "library exists".
    return false;
  }
};
